import '../../index.css';
import './Guide.css';

export default function InstructorGuide() {
  return (
    <div className="guide-page guide-page--trainer">

      {/* Header */}
      <header className="guide-header">
        <a href="/login" className="guide-brand" aria-label="Back to Wow Factor login">
          <span className="guide-brand-mark"><img src="/wf-logo.png" alt="" /></span>
          <span>
            <strong>WOW FACTOR</strong>
            <small>Trainer Guide</small>
          </span>
        </a>
        <a className="guide-login-link" href="/login">Back to portal <span aria-hidden="true">↗</span></a>
      </header>

      <main className="guide-shell">

        {/* Intro */}
        <section className="guide-hero">
          <div className="guide-hero-copy">
            <span className="guide-eyebrow">Trainer Portal · Operations Guide</span>
            <h1>TRAINER<br /><span>REFERENCE.</span></h1>
            <p>
              Welcome to the <strong>Wow Factor Baseball</strong> trainer portal. This guide covers everything you need to manage your profile, availability, session requests, and schedule.
            </p>
          </div>
          <div className="guide-hero-note">
            <span>Quick reference</span>
            <strong>Choose a section below</strong>
            <p>Tap any card to expand it. Tap again when you are finished reading.</p>
          </div>
        </section>

        <div className="guide-intro-callout">
          <InfoBox>Your account is created by an administrator — no email verification required. Log in with the credentials provided to you at <strong>wowfactortraining.com</strong>.</InfoBox>
        </div>

        <div className="guide-section-list">

        <Section title="Your Dashboard">
          <p>When you log in you land on the <Strong>Instructor Dashboard</Strong>, giving you an at-a-glance view of:</p>
          <Ul items={[
            'Pending sessions awaiting your confirmation',
            'Today\'s upcoming sessions',
            'Overall stats — completed, cancelled, upcoming',
            'Recent session activity',
          ]} />
          <WarnBox>If parents are waiting on a response, a highlighted notice appears at the top of your dashboard. Confirm or decline promptly so parents aren't left waiting.</WarnBox>
        </Section>

        <Section title="Setting Up Your Profile">
          <p>Go to <Strong>My Profile</Strong> in the sidebar. Your profile is visible to parents when they browse instructors.</p>
          <SubSection title="Profile Fields">
            <Ul items={[
              'Bio — introduce yourself, your background, and coaching philosophy',
              'Specialties — list your focus areas (e.g. Pitching mechanics, Hitting, Fielding)',
              'Session duration — set your default length (30 or 60 minutes, or both)',
              'Min notice hours — minimum advance notice required before a parent can book',
              'Max advance booking days — how far ahead parents can schedule',
              'Accepting bookings — toggle on/off to open or close your schedule',
            ]} />
          </SubSection>
          <SubSection title="Profile Photo">
            <p>Upload a professional photo under the profile section. Supported: JPEG, PNG, WebP, HEIC. Your photo appears on your instructor card when parents browse.</p>
          </SubSection>
          <TipBox>A complete profile with a photo, bio, and clear specialties significantly increases parent confidence when booking.</TipBox>
        </Section>

        <Section title="Managing Your Availability">
          <p>Go to <Strong>Availability</Strong> in the sidebar to control when parents can book sessions with you.</p>
          <SubSection title="Weekly Schedule (Recurring Slots)">
            <p>Set regular weekly time windows for each day. For example: <em>Monday 9:00 AM – 12:00 PM</em>. The system generates bookable slots within each window based on your session duration.</p>
            <Ul items={[
              'Click + Add Slot to add a new time window',
              'Select the day(s), start time, and end time',
              'Remove a slot with the ✕ button',
            ]} />
          </SubSection>
          <SubSection title="Exceptions (Blocking Specific Dates)">
            <p>Use <Strong>Exceptions</Strong> to block specific dates or time windows — holidays, vacations, personal days. Exceptions override your weekly schedule.</p>
            <Ul items={[
              'Leave time fields blank to block an entire day',
              'Enter start and end times to block only a specific window on that date',
              'Optionally add a reason for your own reference',
            ]} />
            <InfoBox>Adding an exception does not cancel already-booked sessions on that date. You'll need to cancel or reschedule those separately.</InfoBox>
          </SubSection>
        </Section>

        <Section title="Managing Sessions">
          <p>Go to <Strong>My Sessions</Strong> in the sidebar to view and manage all bookings. Filter by status and search by parent or player name.</p>
          <SubSection title="Session Statuses & Available Actions">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'10px', margin:'12px 0 16px' }}>
              {[
                ['Pending',   '#fef9c3', '#854d0e', 'New booking request — needs your action'],
                ['Confirmed', '#dcfce7', '#14532d', 'You\'ve accepted — session is scheduled'],
                ['Completed', '#dbeafe', '#1e3a5f', 'Session marked as done'],
                ['Cancelled', '#fee2e2', '#7f1d1d', 'Cancelled by you or the parent'],
                ['No Show',   '#f3f4f6', '#374151', 'Parent did not attend'],
              ].map(([label, bg, color, desc]) => (
                <div key={label} style={{ background:bg, borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color, marginBottom:'4px' }}>{label}</div>
                  <div style={{ fontSize:'0.8rem', color:'#555' }}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--border)', background:'var(--bg-elevated)' }}>
                    {['Current Status', 'Available Actions', 'Notes'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:'0.7rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--text-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Pending', 'Confirm · Cancel · Reschedule', 'Confirm to lock it in; Cancel to decline and free the slot'],
                    ['Confirmed', 'No Show · Cancel · Reschedule', 'Sessions complete automatically after the end time. Mark No Show if parent didn\'t arrive.'],
                    ['Completed / Cancelled / No Show', 'View only', 'Final states — no further action available'],
                  ].map(([status, actions, notes]) => (
                    <tr key={status} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'10px 14px', fontWeight:'600', color:'var(--text-primary)', whiteSpace:'nowrap' }}>{status}</td>
                      <td style={{ padding:'10px 14px', color:'var(--text-secondary)' }}>{actions}</td>
                      <td style={{ padding:'10px 14px', color:'var(--text-muted)', fontSize:'0.8rem' }}>{notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SubSection>
          <SubSection title="Adding Notes to a Session">
            <p>Click <Strong>View</Strong> on any session to open the detail panel. Use the notes field to record coaching observations or parent instructions. Notes are visible to admins.</p>
          </SubSection>
        </Section>

        <Section title="Rescheduling a Session">
          <p>Click <Strong>Reschedule</Strong> next to any pending or confirmed session. Select a new date and time, then confirm.</p>
          <Ul items={[
            'The parent is automatically notified of the change',
            'The session returns to Pending status until you confirm it',
            'When you confirm, the parent receives a single combined notification showing the old time and new time',
            'The system checks for conflicts — you cannot reschedule to an already-booked slot',
          ]} />
          <TipBox>Reschedule and immediately confirm so the parent gets the combined rescheduled & confirmed notification in one message.</TipBox>
        </Section>

        <Section title="Notifications You Will Receive">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', margin:'12px 0' }}>
            {[
              ['New Booking Request', 'Email when a parent books a session. Their contact info is included.'],
              ['Session Cancelled', 'Email + SMS when a parent cancels. Includes any reason they provided.'],
              ['24-Hour Reminder', 'Email the morning before any confirmed session (sent at 9 AM ET daily).'],
              ['Notification Preferences', 'Manage email and SMS preferences under My Profile. Phone required for SMS.'],
            ].map(([title, desc]) => (
              <div key={title} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'14px' }}>
                <div style={{ fontWeight:'700', fontSize:'0.8rem', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'6px' }}>{title}</div>
                <div style={{ fontSize:'0.825rem', color:'var(--text-secondary)', lineHeight:'1.5' }}>{desc}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Using the Calendar">
          <p>The <Strong>Calendar</Strong> view shows all your sessions by week. Click any session to view its details. Use the arrows to navigate between weeks — useful for spotting gaps, back-to-backs, or busy periods at a glance.</p>
        </Section>

        <Section title="Getting Help & Support">
          <p>If you run into any issue — a technical problem, a question about the platform, or anything else — you can reach the support team directly from inside your portal without leaving the site.</p>
          <SubSection title="Opening a Support Ticket">
            <Ul items={[
              'Click Support in the sidebar',
              'Click + New Ticket',
              'Enter a short Subject describing your issue',
              'Write your full message in the Message field with as much detail as possible',
              'Click Submit Ticket — the admin team is notified right away',
            ]} />
          </SubSection>
          <SubSection title="When the Team Replies">
            <p>You'll be alerted in two places as soon as someone responds:</p>
            <Ul items={[
              'A blue notification banner appears at the top of your Dashboard home page',
              'A red badge with the unread count appears on the Support item in the sidebar',
            ]} />
            <p style={{ marginTop:'8px' }}>Both clear automatically when you open the Support tab.</p>
          </SubSection>
          <SubSection title="Replying & Following Up">
            <p>Click any ticket in the Support list to view the full conversation thread. Messages are displayed in a chat-style view — your messages on the right, support responses on the left. Use the reply box at the bottom to respond; press Ctrl+Enter or ⌘+Enter to send quickly.</p>
          </SubSection>
          <SubSection title="Ticket Statuses">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'10px', margin:'12px 0' }}>
              {[
                ['Open', 'rgba(251,191,36,0.15)', '#b45309', 'Submitted — awaiting a response'],
                ['In Progress', 'rgba(96,165,250,0.15)', '#1d4ed8', 'Admin is actively looking into it'],
                ['Resolved', 'rgba(74,222,128,0.15)', '#15803d', 'Issue closed — no further action needed'],
              ].map(([label, bg, color, desc]) => (
                <div key={label} style={{ background:bg, borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color, marginBottom:'4px' }}>{label}</div>
                  <div style={{ fontSize:'0.8rem', color:'#555' }}>{desc}</div>
                </div>
              ))}
            </div>
          </SubSection>
          <WarnBox>Once a ticket is marked Resolved, the reply box closes. If the problem persists, open a new ticket — mentioning the original ticket number helps the team track the history.</WarnBox>
          <TipBox>Use support tickets for platform questions. For scheduling changes with a specific parent, reach out to them directly via the contact info on their session card.</TipBox>
        </Section>

        <Section title="Frequently Asked Questions">
          {[
            ['A parent booked a session but I can\'t make it. What should I do?', 'Reschedule to a time that works for both of you, or cancel it. If you cancel, the parent is notified immediately. Always communicate with the parent when possible before making changes.'],
            ['How do I temporarily stop accepting new bookings?', 'Go to My Profile and toggle Accepting Bookings to off. Your profile is hidden from the booking list until you turn it back on. Existing sessions are not affected.'],
            ['A parent booked over a time I blocked as an exception.', 'Exceptions only block new bookings going forward. If a session was booked before you added the exception, it remains active and must be cancelled or rescheduled manually.'],
            ['What\'s the difference between Cancel and No Show?', 'Cancel — the session is called off in advance and both parties are notified. No Show — the parent missed the session after the scheduled time; they receive an email notification. Use No Show after the scheduled time has passed.'],
            ['Can I see contact info for parents who book with me?', 'Yes. Click View on any session to see the parent\'s phone number and email address in the detail panel.'],
            ['How do session reminders work?', 'Every morning at 9 AM Eastern, the system automatically sends reminders to both you and the parent for confirmed sessions the following day. No action needed.'],
            ['I forgot my password.', 'On the login page click Forgot your password? and enter your email. You\'ll receive a reset link valid for 1 hour. If you don\'t receive it, email us at info@wowfactortraining.com.'],
            ['How do I report a platform issue or ask an admin a question?', 'Click Support in the sidebar and open a new ticket. Describe the issue clearly and submit. You\'ll see a badge on the Support nav item and a banner on your Dashboard when the team replies — no email needed.'],
            ['I replied to a support ticket but can\'t send any more messages.', 'The ticket was marked Resolved. If the issue isn\'t fully fixed, open a new ticket and reference your previous ticket number so the team can pick up where things left off.'],
          ].map(([q, a]) => (
            <div key={q} style={{ borderBottom:'1px solid var(--border)', paddingBottom:'16px', marginBottom:'16px' }}>
              <div style={{ fontWeight:'700', fontSize:'0.9rem', marginBottom:'6px' }}>{q}</div>
              <div style={{ color:'var(--text-secondary)', fontSize:'0.875rem', lineHeight:'1.6' }}>{a}</div>
            </div>
          ))}
        </Section>
        </div>
      </main>

      <footer className="guide-footer">
        <strong>Wow Factor Baseball — Trainer Portal</strong>
        <span>wowfactortraining.com</span>
        <span>Questions? <a href="mailto:info@wowfactortraining.com">info@wowfactortraining.com</a></span>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <details className="guide-section-card">
      <summary>
        <span className="guide-section-number" aria-hidden="true" />
        <span className="guide-section-title">{title}</span>
        <span className="guide-section-action" aria-hidden="true" />
      </summary>
      <div className="guide-section-body">{children}</div>
    </details>
  );
}
function SubSection({ title, children }) {
  return (
    <div className="guide-subsection">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
function Strong({ children }) {
  return <strong className="guide-strong">{children}</strong>;
}
function Ul({ items }) {
  return (
    <ul className="guide-list">
      {items.map(i => <li key={i}>{i}</li>)}
    </ul>
  );
}
function InfoBox({ children }) {
  return <div className="guide-callout guide-callout--info"><span aria-hidden="true">i</span><div>{children}</div></div>;
}
function TipBox({ children }) {
  return <div className="guide-callout guide-callout--tip"><span aria-hidden="true">✓</span><div>{children}</div></div>;
}
function WarnBox({ children }) {
  return <div className="guide-callout guide-callout--warn"><span aria-hidden="true">!</span><div>{children}</div></div>;
}
